import React, { useState, useEffect, useRef } from 'react';
import { getChatMessages, postMessage } from '../api';

// Format timestamp to local time and adjust for user's timezone
const formatTimestamp = (timestamp) => {
  // Log the timestamp for debugging
  console.log('Original timestamp:', timestamp);
  
  try {
    // The timestamp from the server is in ISO format but may not include timezone
    // Make sure we're interpreting it correctly based on how it was created
    
    // If the timestamp doesn't include a 'Z' (UTC indicator) or timezone offset
    // Force interpretation as UTC since that's likely what the server uses
    const date = timestamp.endsWith('Z') || timestamp.includes('+') || timestamp.includes('-') 
      ? new Date(timestamp) // Already has timezone info
      : new Date(timestamp + 'Z'); // Explicitly add UTC indicator
      
    console.log('Parsed date object:', date);
    console.log('Local time string:', date.toString());
    
    // Get timezone offset in minutes
    const offsetMinutes = date.getTimezoneOffset();
    console.log('Timezone offset (minutes):', offsetMinutes);
    
    // Format options with explicit timezone
    const options = { 
      hour: 'numeric', 
      minute: 'numeric',
      hour12: true
    };
    
    // Add date if the message is not from today
    const today = new Date();
    if (date.toDateString() !== today.toDateString()) {
      options.month = 'short';
      options.day = 'numeric';
    }
    
    // Format using the user's locale
    const formattedTime = date.toLocaleTimeString('en-US', options);
    console.log('Formatted time:', formattedTime);
    
    return formattedTime;
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    // Fall back to a simple format if there's an error
    return timestamp;
  }
};

// Chat avatar component for displaying user profile pictures
const ChatAvatar = ({ username, profilePictureUrl }) => {
  // Generate initials from username
  const initials = username ? username[0].toUpperCase() : '?';
  
  return (
    <div className="chat-image avatar">
      <div className="w-10 rounded-full ring ring-primary ring-offset-base-100 ring-offset-1">
        {profilePictureUrl ? (
          <img src={profilePictureUrl} alt={username} />
        ) : (
          <div className="bg-neutral-focus text-neutral-content w-full h-full flex items-center justify-center">
            <span>{initials}</span>
          </div>
        )}
      </div>
    </div>
  );
};

function Chat() {
  const [chatMessages, setChatMessages] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [error, setError] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Fetch chat messages when component mounts
    fetchChatMessages();
    
    // Set up periodic refresh for chat messages
    const interval = setInterval(() => {
      fetchChatMessages();
    }, 5000); // Refresh every 5 seconds
    
    setRefreshInterval(interval);
    
    // Cleanup
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, []);
  
  // Scroll to bottom on initial load and when a new message is sent by the current user
  useEffect(() => {
    // Initial scroll to bottom when messages first load
    if (messagesEndRef.current) {
      scrollToBottom();
    }
  }, []); // Empty dependency array means this runs once on mount
  
  // When sending a new message, scroll to bottom
  useEffect(() => {
    if (chatMessages.length > 0 && chatMessages[chatMessages.length - 1].isCurrentUser) {
      scrollToBottom();
    }
  }, [chatMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const fetchChatMessages = async () => {
    try {
      const messages = await getChatMessages();
      setChatMessages(messages);
      setError('');
    } catch (error) {
      console.error("Error fetching chat messages:", error);
      setError("Failed to load chat messages. Please try again later.");
    }
  };

  const handleSubmitMessage = async () => {
    if (!userMessage.trim()) return;
    
    try {
      setError('');
      await postMessage(userMessage);
      setUserMessage(''); // Clear input after sending
      await fetchChatMessages(); // Refresh messages and await the result
    } catch (error) {
      setError(error.message || "Failed to send message. Please try again.");
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmitMessage();
    }
  };

  return (
    <div className="flex flex-col h-screen max-h-screen">
      <div className="p-4 bg-base-300 shadow-md">
        <h1 className="text-2xl font-bold">Chat Application</h1>
      </div>
      
      <div className="flex-1 flex flex-col p-4 bg-base-100 overflow-hidden">
        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}
        
        <div className="flex-1 overflow-y-auto mb-4 rounded-lg p-4 bg-base-200 shadow-inner">
              {chatMessages.length === 0 ? (
                <div className="text-center text-base-content opacity-50 my-10">
                  No messages yet. Be the first to say hello!
                </div>
              ) : (
                chatMessages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`chat ${msg.isCurrentUser ? 'chat-end' : 'chat-start'} mb-6`}
                  >
                    <ChatAvatar 
                      username={msg.username} 
                      profilePictureUrl={msg.profilePictureUrl} 
                    />
                    <div className="chat-header">
                      {msg.username}
                      <time className="text-xs opacity-50 ml-1">
                        {formatTimestamp(msg.timestamp)}
                      </time>
                    </div>
                    <div className={`chat-bubble ${msg.isCurrentUser ? 'chat-bubble-primary' : 'bg-base-300'} shadow-md`}>
                      {msg.message}
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
        </div>
        
        <div className="p-4 bg-base-300 rounded-lg shadow-md">
          <div className="flex items-center mb-2">
            <button 
              className="btn btn-sm btn-outline mr-2" 
              onClick={scrollToBottom}
            >
              Scroll Down
            </button>
            <button 
              className="btn btn-sm btn-outline" 
              onClick={fetchChatMessages}
            >
              Refresh
            </button>
          </div>
          
          <div className="join w-full">
            <input
              type="text"
              className="input input-bordered join-item flex-1"
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type your message here..."
            />
            <button 
              className="btn btn-primary join-item" 
              onClick={handleSubmitMessage}
              disabled={!userMessage.trim()}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chat;
